import type { GenericInheritanceCircuitLever } from '@shared/services/transmission/types'
import { useCurrentUser } from '@shared/stores/current-user'
import {
  ENDPOINTS,
  INCLUDES,
  QUERY_PARAMS,
  computeUrlWithQueryParameters,
  type PortfoliosIncludes,
} from '@shared/utils'
import { globalStoreForLogging } from 'src/App.utils'
import type { AppContext } from 'src/global-types'
import apiClient from './api/api-client.service'
import { setApiUrl } from 'src/services/AppContext'
import { removeRecordFromCache } from 'src/services/deserializers/recordStore'
import {
  jsonApi,
  requestTokenWithPassword,
} from 'src/services/json-api/json-api.service'
import { leversMapper } from 'src/services/mappers/mappers'
import type { PortfoliosMeta } from 'src/services/mappings/portfolio/portfolio.types'
import { ENGINES } from 'src/services/people/peopleApiService.const'
import { cleanRecord } from 'src/services/record.utils'
import { serialize2 } from 'src/services/serializers/serializers'
import {
  OBJECT_TYPES,
  type ObjectType,
} from 'src/tpbp-configuration/models/models.const'
import { FEATURE_IDS } from 'src/tpbp-core/feature-flag/featureFlags.const'
import {
  getFeatureFlags,
  hasFeature,
  setConfigurationFeatureFlag,
  setPlatform,
} from 'src/tpbp-core/feature-flag/featureFlags.utils'
import type {
  PersonModel,
  PortfolioModel,
} from 'src/tpbp-json-api-handler/models/models'
import { logger } from 'src/utils/logger.utils'
import { getNewRelicParameters } from './NewRelic.helper'
import { type ProxyRecord } from './deserializers/deserializers.types'
import type { TokenSet } from './json-api/json-api.types'
import type { MessageIndicator } from './mappings/indicator/indicator.types'
import {
  buildPersonCircuitsUrl,
  buildPersonUrl,
  buildStudiesIndicatorUrl,
} from './people/utils/buildPeopleUrl'
import type { GenericCircuitResponse } from './request.types'
import { type ContextConfigFile } from './request.types'

const context = {
  jwtServiceUrl: '',
}

async function getMessageIndicator(
  personId: string,
  attributes?: Record<string, unknown>,
  studyId?: string,
) {
  const payload = {
    type: OBJECT_TYPES.indicator,
    attributes,
  }
  const endpoint = buildStudiesIndicatorUrl(personId, [
    QUERY_PARAMS.engine(ENGINES.messages),
    QUERY_PARAMS.study(studyId),
  ])
  const response = await jsonApi.request<MessageIndicator>(
    endpoint,
    'POST',
    undefined,
    payload,
  )
  if (response instanceof Error) throw response
  return response?.data
}

async function getCircuits<
  T = GenericCircuitResponse<GenericInheritanceCircuitLever[]>[],
>(personId: string, studyId?: string, circuitName = 'inheritance') {
  const queryParameters: string[] = [
    QUERY_PARAMS.filters('circuit', [circuitName]),
    INCLUDES.circuits(['levers']),
  ]
  if (studyId) queryParameters.push(QUERY_PARAMS.study(studyId))
  const endpoint = buildPersonCircuitsUrl(personId, queryParameters)
  return await jsonApi.findAll<T>(endpoint)
}

export function computePortfolioUrl(
  includes: PortfoliosIncludes[] | undefined,
  addExtendedPortfolios: boolean,
  personId: string,
  hasCoupleApproach = hasFeature(FEATURE_IDS.coupleApproach),
) {
  const include = includes ? [INCLUDES.portfolios(includes)] : []
  const extendedPortfolio =
    hasCoupleApproach && addExtendedPortfolios
      ? [QUERY_PARAMS.extended_portfolios(true)]
      : []
  return computeUrlWithQueryParameters(ENDPOINTS.portfolios(personId), [
    ...include,
    ...extendedPortfolio,
  ])
}

async function getPersonPortfolios(
  personId: string,
  include?: PortfoliosIncludes[],
  addExtendedPortfolios = true,
) {
  const url = computePortfolioUrl(include, addExtendedPortfolios, personId)
  const { data, meta } = await jsonApi.findAll<
    PortfolioModel[],
    PortfoliosMeta
  >(url)
  return { portfolios: data, meta }
}

async function getPeople(peopleId: string, include?: string) {
  const url = buildPersonUrl(peopleId, include ? [include] : [])
  return getRequest<PersonModel | undefined>(url)
}

async function getRessource<Type = unknown>(
  type: ObjectType,
  id?: string,
  inputQuery?: string,
) {
  const endpoint = ENDPOINTS.recordType(type, id)
  const url = computeUrlWithQueryParameters(
    endpoint,
    inputQuery ? [inputQuery] : [],
  )
  const response = await jsonApi.request<Type>(url, 'GET')
  if (response instanceof Error) throw response
  return response.data
}

async function getRequest<Type = unknown>(
  url: string,
  queryParameters: string[] = [],
) {
  const endpoint = computeUrlWithQueryParameters(url, queryParameters)
  const response = await jsonApi.request<Type>(endpoint, 'GET')
  if (response instanceof Error) throw response
  return response.data
}

async function postRequest<Type = unknown>(
  url: string,
  queryParameters: string[] = [],
  payload?: object,
) {
  const endpoint = computeUrlWithQueryParameters(url, queryParameters)
  const response = await jsonApi.request<Type>(
    endpoint,
    'POST',
    undefined,
    payload,
  )
  if (response instanceof Error) throw response
  return response.data
}

async function patchRequest<Type = unknown>(
  url: string,
  queryParameters: string[] = [],
  payload?: object,
) {
  const endpoint = computeUrlWithQueryParameters(url, queryParameters)
  const response = await jsonApi.request<Type>(
    endpoint,
    'PATCH',
    undefined,
    payload,
  )
  if (response instanceof Error) throw response
  return response.data
}

async function deleteRequest<Type = unknown>(
  url: string,
  queryParameters: string[] = [],
  payload?: object,
) {
  const endpoint = computeUrlWithQueryParameters(url, queryParameters)
  const response = await jsonApi.request<Type>(
    endpoint,
    'DELETE',
    undefined,
    payload,
  )
  if (response instanceof Error) throw response
  return response.data
}

async function getSubList(
  type1: string,
  id1: string,
  type2: string,
  id2?: string,
  query?: string,
) {
  const baseUrl = ENDPOINTS.relationship(type1, id1, type2, id2)
  const url = computeUrlWithQueryParameters(baseUrl, query ? [query] : [])
  return getRequest(url)
}

async function validateNniToken(token: string): Promise<TokenSet> {
  const url = `${context.jwtServiceUrl}/${ENDPOINTS.tokens()}/validate_nni`
  const response = await apiClient.post<{ access_token: TokenSet }>(url, {
    nni_token: token,
  })
  const accessToken = response?.data?.access_token
  if (!accessToken) throw new Error('Found no access token in response')
  return accessToken
}

function connect(username: string, password: string) {
  return requestTokenWithPassword(username, password)
}

async function getCircuitsLevers(personId: string, studyId: string) {
  const response = await getCircuits(personId, studyId)
  return leversMapper(response)
}

async function loadContext() {
  globalStoreForLogging().startLog('loading-config.json')
  const response = await fetch('./context/config.json', { cache: 'no-cache' })
  globalStoreForLogging().startLog('loading-config.json')

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const data: ContextConfigFile = await response.json()

  if (!['production', 'test'].includes(process.env.NODE_ENV ?? '')) {
    data.API_URL = process.env.VUE_APP_API_URL ?? ''
    data.API_URL_ADVISING_REPORT =
      process.env.VUE_APP_API_URL_ADVISING_REPORT ?? ''
    data.API_URL_SELFCARE = process.env.VUE_APP_API_URL_SELFCARE ?? ''
  }

  context.jwtServiceUrl =
    document.referrer === data.SELFCARE_REFERRER
      ? data.JWT_SERVICE_URL_SELFCARE
      : data.JWT_SERVICE_URL
  setPlatform(data.PLATFORM_ENV)
  setApiUrl(data.API_URL)

  for (const featureId of Object.keys(FEATURE_IDS)) {
    if (Object.hasOwn(data, featureId)) {
      setConfigurationFeatureFlag(featureId, data[featureId])
    }
  }

  const newRelicParameters = getNewRelicParameters(data)
  const featureFlags = getFeatureFlags()
  logger.log({ featureFlags })
  const _context = {
    bypassJwtRopc: data.BYPASS_JWT_ROPC,
    apiUrl: data.API_URL,
    apiUrlAdvisingReport: data.API_URL_ADVISING_REPORT,
    apiUrlSelfcare: data.API_URL_SELFCARE,
    raygunApiKey: data.raygunApiKey,
    geoapifyKey: data.geoapify_key,
    subscriptionLink: data.subscriptionLink,
  } satisfies AppContext

  return {
    context: _context,
    newRelicParameters,
  }
}

async function createRecord<Type = unknown>(
  record: ProxyRecord,
  include?: string,
  refreshRelations = false,
) {
  const parameters = include ? { include } : null
  try {
    record.$clearErrors()
    const created = await jsonApi.create(record.$type, record, parameters)
    if (created?.data) {
      record.id = created.data.id
      record.$isNew = false
      record.$attributes = created.data.$attributes
      record.$attributesOrigin = {}
      if (refreshRelations) {
        record.$relationships = created.data.$relationships
      }
    }
    return created?.data as Type
  } catch (error) {
    if (jsonApi._lastErrors) record.$setErrors(jsonApi._lastErrors)
    throw error
  }
}

function deleteRecord(record: ProxyRecord) {
  removeRecordFromCache(record)
  const url = ENDPOINTS.recordType(record.$type, record.id)
  return deleteRequest(url)
}

function saveRecord<T extends ProxyRecord>(
  record: ProxyRecord,
  include?: string,
): Promise<T> {
  if (record.$isNew) return createRecord<T>(record, include)
  return updateRecord<T>(record, include)
}

async function updateRecord<T extends ProxyRecord = ProxyRecord>(
  record: ProxyRecord,
  include?: string,
  updateRelationships = true,
): Promise<T> {
  if (record.$type === OBJECT_TYPES.person) {
    cleanRecord(
      record,
      false,
      useCurrentUser().temporarilyFullAccessCurrentUser.value,
    )
  }
  const parameters = include ? { include } : null
  try {
    record.$clearErrors()
    if (!record.$hasChanged && !record.$relationshipsHasChanged) {
      // nothing to save
      return record as T
    }
    if (!serialize2(record)) {
      // find a better way to detect changes (when relationships must be save by an other endpoint)
      return record as T
    }
    const updated = await jsonApi.update(record.$type, record, parameters)
    if (updated?.data) {
      record.$attributes = updated.data.$attributes
      record.$meta = updated.data.$meta
      if (updateRelationships) {
        record.$relationships = updated.data.$relationships
      }
    }
    record.$attributesOrigin = {}
    record.$hasChanged = false
    record.$relationshipsDeleted = {}
    record.$relationshipsAdded = {}
    record.$relationshipsHasChanged = false
    return (updated?.data ?? record) as T
  } catch (error) {
    if (jsonApi._lastErrors) record.$setErrors(jsonApi._lastErrors)
    throw error
  }
}

async function getDocument(documentUrl: string): Promise<Blob | undefined> {
  const config = {
    responseType: 'blob' as const,
  }
  const response = await apiClient.get<Blob>(documentUrl, config)
  if (response instanceof Error) return
  return response.data
}

export const requestService = {
  connect,
  createRecord,
  deleteRecord,
  getMessageIndicator,
  getCircuits,
  getCircuitsLevers,
  getDocument,
  getPeople,
  getPersonPortfolios,
  getSubList,
  loadContext,
  getRequest,
  postRequest,
  patchRequest,
  deleteRequest,
  saveRecord,
  updateRecord,
  validateNniToken,
  getRessource,
}

export type RequestService = typeof requestService
